/**
 * Variant Capability Schema
 * 
 * Single source of truth for variant support across all doctypes.
 * Defines what variant modes are supported, how they work, and what backend methods to call.
 * 
 * AI-FIRST: This schema is the complete contract for variant support.
 * 
 * Performance: Schema is cached at module level (zero runtime cost).
 */

import type { VariantMode } from './types';

/**
 * UI step in variant creation flow
 */
export type VariantUIStep = {
  /** Step identifier */
  step: string;
  /** Human-readable label */
  label: string;
  /** Description for UI tooltips */
  description: string;
  /** Component name to render (for reference, not used at runtime) */
  component: string;
  /** Whether step is required */
  required: boolean;
};

/**
 * Abbreviation generation rules
 * 
 * Defines how abbreviations are generated from attribute values for variant codes.
 * Single source of truth for abbreviation logic.
 */
export type AbbreviationRules = {
  /** Maximum abbreviation length (ERPNext constraint) */
  maxLength: number;
  /** Fallback abbreviation when input is empty or generation fails */
  fallback: string;
  /** Strategy for multi-word values: "first_letter_each" takes first letter of each word */
  multiWordStrategy: 'first_letter_each';
  /** Strategy for single-word values ≤ shortThreshold chars: "as_is" uses value as-is */
  singleWordShortStrategy: 'as_is';
  /** Strategy for single-word values > shortThreshold chars: "first_n_chars" takes first n chars */
  singleWordLongStrategy: 'first_n_chars';
  /** Threshold for "short" single-word values (≤ this uses short strategy) */
  shortThreshold: number;
  /** Number of characters to take for long single-word values */
  longCharCount: number;
};

/**
 * Variant capability configuration
 * Defines how a variant mode works
 */
export type VariantCapability = {
  /** Mode identifier */
  mode: VariantMode;
  
  /** Human-readable label */
  label: string;
  
  /** Description for UI tooltips */
  description: string;
  
  /** Required fields in template Item */
  requiredFields: string[];
  
  /** Child table that holds variant config (e.g., 'attributes' for item_attribute) */
  configTable: string;
  
  /** Backend method to check if variant exists */
  getVariantMethod: string;
  
  /** Backend method to create single variant */
  createVariantMethod: string;
  
  /** Backend method to create multiple variants (queued) */
  enqueueMultipleMethod: string;
  
  /** Max variants before queueing (if > this, use enqueueMultipleMethod) */
  maxSyncVariants: number;
  
  /** Max total variants allowed */
  maxTotalVariants: number;
  
  /** UI flow steps */
  uiSteps: VariantUIStep[];
  
  /** Abbreviation generation rules for variant codes */
  abbreviationRules: AbbreviationRules;
};

/**
 * Variant Capability Schema
 * 
 * Single source of truth for all variant modes.
 * Add new modes here → automatically works everywhere.
 */
export const VARIANT_CAPABILITY_SCHEMA: Record<VariantMode, VariantCapability> = {
  item_attribute: {
    mode: 'item_attribute',
    label: 'Item Attribute',
    description: 'Create variants based on attributes (Size, Color, Material, etc.)',
    requiredFields: ['variant_based_on', 'attributes'],
    configTable: 'attributes',
    getVariantMethod: 'erpnext.controllers.item_variant.get_variant',
    createVariantMethod: 'erpnext.controllers.item_variant.create_variant',
    enqueueMultipleMethod: 'erpnext.controllers.item_variant.enqueue_multiple_variant_creation',
    maxSyncVariants: 10,
    maxTotalVariants: 500,
    uiSteps: [
      {
        step: 'select_attributes',
        label: 'Select Attributes',
        description: 'Choose which attributes define variants',
        component: 'AttributeSelector',
        required: true,
      },
      {
        step: 'select_values',
        label: 'Select Values',
        description: 'Choose values for each attribute',
        component: 'ValueSelector',
        required: true,
      },
      {
        step: 'preview',
        label: 'Preview Variants',
        description: 'Review variant count and codes',
        component: 'VariantPreview',
        required: false,
      },
    ],
    abbreviationRules: {
      maxLength: 5,
      fallback: 'VAL',
      multiWordStrategy: 'first_letter_each',
      singleWordShortStrategy: 'as_is',
      singleWordLongStrategy: 'first_n_chars',
      shortThreshold: 3,
      longCharCount: 3,
    },
  },
  // Note: manufacturer mode removed for Phase 1
  // Will be added in Phase 4 when manufacturer variant creation is implemented
  // manufacturer: {
  //   mode: 'manufacturer',
  //   label: 'Manufacturer',
  //   description: 'Create variants based on manufacturer and part number',
  //   requiredFields: ['variant_based_on', 'manufacturer'],
  //   configTable: 'manufacturer',
  //   getVariantMethod: 'erpnext.controllers.item_variant.get_variant',
  //   createVariantMethod: 'erpnext.controllers.item_variant.get_variant', // Uses get_variant with manufacturer args
  //   enqueueMultipleMethod: 'erpnext.controllers.item_variant.enqueue_multiple_variant_creation',
  //   maxSyncVariants: 10,
  //   maxTotalVariants: 500,
  //   uiSteps: [...],
  // },
};

/**
 * Get variant capability for a mode
 * Cached lookup (zero runtime cost after first call)
 */
export function getVariantCapability(mode: VariantMode): VariantCapability {
  return VARIANT_CAPABILITY_SCHEMA[mode];
}

/**
 * Get all supported variant modes
 */
export function getSupportedVariantModes(): VariantMode[] {
  return Object.keys(VARIANT_CAPABILITY_SCHEMA) as VariantMode[];
}
